import { Filters } from 'components/shared/CASTTable/useCASTTable';
import { IODataTableRecord } from 'types/models/IODataTableRecord';
import { create } from 'zustand';
import {
  createFilterStore,
  EntityFilter,
  Filter,
  FilterStoreState,
  TableFilterOperationLookup,
} from './filterStore';

interface PredictEntityFilter
  extends Omit<EntityFilter, 'getBuildDataFilterFunction' | 'setJoinOperator' | 'joinOperator'> {
  filter: {
    externalId: Filter[];
    address: Filter[];
    zone: Filter[];
    utilityInspectionStatus: Filter[];
    privateInspectionStatus: Filter[];
    modelLeadStatus: Filter[];
    modelUtilityStatus: Filter[];
    modelUtilityMethod: Filter[];
    modelPrivateStatus: Filter[];
    modelPrivateMethod: Filter[];
    featureStratification: Filter[];
  };
  resetFilters: () => void;
  setFilterState: (filters: Filters<IODataTableRecord>) => void;
}

export interface PredictFilterSlice extends FilterStoreState {
  predict: PredictEntityFilter;
}

const filterDefault = {
  externalId: [] as Filter[],
  address: [] as Filter[],
  zone: [] as Filter[],
  utilityInspectionStatus: [] as Filter[],
  privateInspectionStatus: [] as Filter[],
  modelLeadStatus: [] as Filter[],
  modelUtilityStatus: [] as Filter[],
  modelUtilityMethod: [] as Filter[],
  modelPrivateStatus: [] as Filter[],
  modelPrivateMethod: [] as Filter[],
  featureStratification: [] as Filter[],
};

const initialState = (set, get) => ({
  predict: {
    filter: filterDefault,
    getQueryParamRepresentation: (respectGlobalFilters = true) =>
      get().getQueryParamRepresentationByEntity('predict', ['properties'])(respectGlobalFilters),
    resetFilters() {},
    setFilterState(filters: Filters<IODataTableRecord>) {
      const newFilterState: PredictEntityFilter['filter'] = {
        externalId: [],
        address: [],
        zone: [],
        utilityInspectionStatus: [],
        privateInspectionStatus: [],
        modelLeadStatus: [],
        modelUtilityStatus: [],
        modelUtilityMethod: [],
        modelPrivateStatus: [],
        modelPrivateMethod: [],
        featureStratification: [],
      };
      Object.entries(filters).forEach(([key, { value, operator }]) => {
        const mappedOperator = TableFilterOperationLookup[operator];
        if (value && mappedOperator) {
          // Escape commas in values where operator is 'any'
          const escapedValue =
            mappedOperator === 'any'
              ? Array.isArray(value)
                ? value.map((val) => escapeCommas(String(val)))
                : escapeCommas(String(value))
              : value;

          newFilterState[key] =
            typeof mappedOperator === 'string'
              ? [{ value: escapedValue, operator: mappedOperator }]
              : [
                  { value: escapedValue[0], operator: mappedOperator[0] },
                  { value: escapedValue[1], operator: mappedOperator[1] },
                ];
        }
      });
      set((prevState) => ({
        predict: {
          ...prevState.predict,
          filter: newFilterState,
        },
      }));
    },
  },
});

const usePredictFilterStore = create<PredictFilterSlice>((set, get) =>
  createFilterStore<PredictFilterSlice>(set, get, initialState(set, get))
);

function escapeCommas(value: string): string {
  // Applies backslash as an escape character for commas
  // Replace '\' with '\\'
  // Replace ',' with '\,'
  return value.replace(/\\/g, '\\\\').replace(/,/g, '\\,');
}

export default usePredictFilterStore;
